import React from 'react';

import { CurrencyInput } from 'src/app/components/form-elements/inputs/currency-input';
import { FormSection } from 'src/app/components/lib/form';
import { Input } from 'src/app/components/lib/input';
import { MenuItem } from 'src/app/components/lib/menu-item';
import { Select } from 'src/app/components/lib/select';
import { WizardCard } from 'src/app/components/lib/wizard/card';
import { LD } from 'src/app/constants/launch-darkly-flags';
import { useLoyaltyBoosters } from 'src/app/hooks/discount/use-loyalty-boosters';
import { useLDFlag } from 'src/app/hooks/use-ld-flag';

import {
  CalculationMethods,
  manualDefaultApplyToMethods,
  manualDefaultApplyPercentageToMethods,
} from '../../../models';
import { amountCalculationMethods, percentCalculationMethods } from '../../shared/helpers';
import { useDiscountWizardStateContext } from '../../state';
import { RewardsStepActions } from '../../state/rewards';

import { useDiscountAppliesToOptions } from './use-discount-applies-to-options';
import { useDiscountMethods } from './use-discount-methods';

type DiscountApplicationCardProps = {
  markComplete: () => void;
  markStepIncomplete: () => void;
};

export function DiscountApplicationCard({ markStepIncomplete, markComplete }: DiscountApplicationCardProps) {
  const {
    isManualDiscount,
    rewardsStepDispatch,
    rewardsStepState: {
      amountOff,
      discountAppliesTo,
      discountMethod,
      loyaltyMultiplier,
      manualApplyToMethod,
      percentOff,
    },
  } = useDiscountWizardStateContext();

  const useDiscountsLoyaltyMultiplierFix = useLDFlag(LD.DISCOUNTS_LOYALTY_MULTIPLIER_FIX, false);
  const allowLoyaltyBoosters = useLoyaltyBoosters();
  const discountMethods = useDiscountMethods(allowLoyaltyBoosters);

  const { disabledTooltip, discountAppliesToOptions } = useDiscountAppliesToOptions();

  const showAmountOffInput = amountCalculationMethods.includes(discountMethod);
  const amountOffLabel =
    discountMethod === CalculationMethods.AmountOff || discountMethod === CalculationMethods.AmountOffTotal
      ? 'Amount off:'
      : 'Price to amount:';

  const showPercentOffInput = percentCalculationMethods.includes(discountMethod);
  const showLoyaltyMultiplier = discountMethod === CalculationMethods.LoyaltyMultiplier;

  const manualDefaultApplyToOptions =
    discountMethod === CalculationMethods.PercentOff
      ? manualDefaultApplyPercentageToMethods
      : manualDefaultApplyToMethods;

  return (
    <WizardCard
      description={
        showLoyaltyMultiplier
          ? 'Earned loyalty points multiplier: 1 implies standard accrual.'
          : 'Specify the reward that will be applied to discount requirements.'
      }
      title='How will it be applied'
    >
      <FormSection>
        <Select
          label='Discount method:'
          labelPlacement='top'
          placeholder='Select a discount method'
          required
          value={discountMethod}
          onChange={({ target }) => {
            rewardsStepDispatch({ type: RewardsStepActions.SET_DISCOUNT_METHOD, payload: Number(target.value) });
            if (target.value === CalculationMethods.LoyaltyMultiplier) {
              rewardsStepDispatch({ type: RewardsStepActions.SET_ALLOW_STACKING, payload: true });
            }
          }}
        >
          {discountMethods.map((method) => (
            <MenuItem footer={method.description} key={method.name} value={method.val} variant='label'>
              {method.name}
            </MenuItem>
          ))}
        </Select>
        {showAmountOffInput && (
          <CurrencyInput
            decimalPlaces={2}
            label={amountOffLabel}
            labelPlacement='top'
            required
            value={amountOff}
            onChange={(value) =>
              rewardsStepDispatch({
                type: RewardsStepActions.SET_AMOUNT_OFF,
                payload: value === '' ? value : Number(value),
              })
            }
          />
        )}
        {showPercentOffInput && (
          <Input
            label='Percent off:'
            labelPlacement='top'
            required
            type='number'
            value={percentOff}
            onChange={({ target }) =>
              rewardsStepDispatch({
                type: RewardsStepActions.SET_PERCENT_OFF,
                payload: target.value === '' ? target.value : Number(target.value),
              })
            }
          />
        )}

        {showLoyaltyMultiplier && !useDiscountsLoyaltyMultiplierFix && (
          <Input
            label='Multiplier:'
            labelPlacement='top'
            required
            type='number'
            value={amountOff}
            onChange={({ target }) => {
              rewardsStepDispatch({
                type: RewardsStepActions.SET_AMOUNT_OFF,
                payload: Number(target.value),
              });
              if (Number(target.value) < 1) {
                markStepIncomplete();
              } else {
                markComplete();
              }
            }}
          />
        )}
        {showLoyaltyMultiplier && useDiscountsLoyaltyMultiplierFix && (
          <Input
            label='Multiplier:'
            labelPlacement='top'
            required
            type='number'
            value={loyaltyMultiplier}
            onChange={({ target }) => {
              rewardsStepDispatch({
                type: RewardsStepActions.SET_LOYALTY_MULTIPLIER,
                payload: target.value === '' ? target.value : Number(target.value),
              });
            }}
          />
        )}
        {isManualDiscount && (
          <Select
            label='Apply reward to:'
            labelPlacement='top'
            placeholder='Select option'
            required
            value={manualApplyToMethod}
            onChange={({ target }) =>
              rewardsStepDispatch({
                type: RewardsStepActions.SET_MANUAL_APPLY_TO_METHOD,
                payload: Number(target.value),
              })
            }
          >
            {manualDefaultApplyToOptions.map((method) => (
              <MenuItem key={method.name} value={method.val}>
                {method.name}
              </MenuItem>
            ))}
          </Select>
        )}
        {!isManualDiscount && (
          <Select
            label='Apply discount to:'
            labelPlacement='top'
            placeholder='Select what the discount applies to'
            required
            value={discountAppliesTo}
            onChange={({ target }) =>
              rewardsStepDispatch({
                type: RewardsStepActions.SET_DISCOUNT_APPLIES_TO,
                payload: Number(target.value),
              })
            }
          >
            {discountAppliesToOptions.map((option) => (
              <MenuItem
                disabled={option.disabled}
                key={option.name}
                tooltip={option.disabled ? disabledTooltip : undefined}
                value={option.val}
              >
                {option.name}
              </MenuItem>
            ))}
          </Select>
        )}
      </FormSection>
    </WizardCard>
  );
}
